<template>
  <div :class="actionModal.type">
    <BModal
      v-model="showModal"
      :title="actionModal.title"
      centered
      class="errorModal"
      hide-backdrop
      :content-class="'content_' + actionModal.type"
      :header-text-variant="variant.text"
      :header-class="variant.color"
      @hidden="close"
      @hide="hide"
    >
      <div class="modal-body break-word p-0">
        <div class="row mb-3">
          <div class="col-12">
            <p>
              {{ actionModal.message }}
            </p>
          </div>
        </div>
        <div v-if="actionModal.secondMessage" class="row mb-3">
          <div class="col-12">
            <p>
              {{ actionModal.secondMessage }}
            </p>
          </div>
        </div>

        <div v-if="actionModal.type === 'error' && actionModal.error">
          <div class="row">
            <div
              style="cursor: pointer"
              class="col-12 d-flex justify-content-between align-items-center apps-header"
              tabindex="0"
              role="button"
              @keydown.enter="showCollapsedError = !showCollapsedError"
              @click="showCollapsedError = !showCollapsedError"
            >
              <div class="d-flex col-10 pt-3 pb-2 m-0 pl-0">
                <span class="d-flex align-items-center"> Details </span>
              </div>

              <div class="col-2 d-flex flex-row-reverse pr-0">
                <PhCaretRight
                  weight="bold"
                  :class="{ rotate_arrow: showCollapsedError }"
                />
              </div>
            </div>
          </div>
          <BCollapse
            id="showCollapsedError"
            v-model="showCollapsedError"
            class="w-100"
          >
            <div class="mb-3 d-inline-flex align-items-center gap-2">
              <code class="error" style="color: #2d2d2d">{{
                actionModal.error
              }}</code>
              <CopyButton :copy-text="actionModal.error" class="flex-grow-0" />
            </div>
            <div v-if="actionModal.contactSupport" class="row mb-3">
              <div class="col-12">
                <StoreLink target="_blank" :href="supportLink"
                  >Contact Support</StoreLink
                >
              </div>
            </div>
          </BCollapse>
        </div>
      </div>

      <template #footer>
        <div class="w-100">
          <StoreButton type="button" class="float-end" @click="close">
            Close
          </StoreButton>
        </div>
      </template>
    </BModal>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { BCollapse, BModal } from "bootstrap-vue-next";
import { useUiStore } from "~/stores/ui/uistore";
import type { TextColorVariant } from "bootstrap-vue-next";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import { PhCaretRight } from "@phosphor-icons/vue";
import StoreLink from "~/components/shared/buttons/StoreLink.vue";
import CopyButton from "~/components/shared/buttons/CopyButton.vue";

const uiStore = useUiStore();
const { actionModal } = storeToRefs(uiStore);

const showCollapsedError = ref(false);

const showModal = computed({
  get: () => uiStore.showActionModal,
  set: (v) => (uiStore.showActionModal = v),
});

const route = useRoute();
const supportLink = computed(() => {
  if (!actionModal.value?.error) {
    return "/support";
  }
  return `/support?errorId=${actionModal.value?.error}&page=${route.fullPath}`;
});

// Update the `variant` computed property
const variant = computed<{ color: string; text: TextColorVariant }>(() => {
  if (actionModal.value?.type === "success") {
    return {
      color: "light-header", // Valid ClassValue
      text: "dark", // Valid TextColorVariant (assuming 'dark' is a valid key)
    };
  } else if (actionModal.value?.type === "error") {
    return {
      color: "error-header", // Valid ClassValue
      text: "light", // Valid TextColorVariant (assuming 'light' is a valid key)
    };
  }

  // Fallback to a valid TextColorVariant (e.g., 'dark' or any other default key)
  return {
    color: "", // You can keep this as an empty string if `ClassValue` allows it
    text: "dark", // Use a valid fallback value like "dark"
  };
});

function close() {
  showModal.value = false;
}

const hide = () => {
  showCollapsedError.value = false;
};
</script>
<!-- scoped - does not work here because the bootstrap modal uses teleports -->
<style lang="scss">
@use "~/sass/components/NoticeModal.scss" as *;
</style>
